//================================================================
//  Component: Dynamic Add Link Live
//================================================================

//  Purpose: This is the live field component for adding a links list

//  Properties:
//    - links = {An array, this is the list of links}
//    - collectionid = {A string, this is the collection id}
//    - fieldpath = {A string, this is the field path}
//    - documentid = {A string, this is the document id}
//    - disabled = {A boolean, this is the disabled state}

//  Example:
//    <DynamicAddLinkLive
//      links={links}
//      collectionid={collectionid}
//      fieldpath={fieldpath}
//      documentid={documentid}
//      disabled={disabled}
//    ></DynamicAddLinkLive>    

//================================================================

//Libraries
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components

//Functions
import WriteDocument from '../../Library/WriteDocument';

//Images
import Add from '../Images/Icon_Add_Teal.svg';
import AddDisabled from '../Images/Icon_AddDisabled_Teal.svg';
import DeleteIcon from '../Images/Icon_DeleteHover_Red.svg';
import DeleteDisabledIcon from '../Images/Icon_DeleteDisabled_Grey.svg';
import successIcon from '../Images/Icon_Save_Teal.svg';
import pendingIcon from '../Images/Icon_LoadingFile_Grey.gif';
import errorIcon from '../Images/Icon_Error_Red.svg';

//CSS

export default function DynamicAddLinkLive({
  links = [],
  collectionid,
  documentid,
  fieldpath,
  disabled = false
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to store the current link
  const [currentLink, setCurrentLink] = useState('');

  // Used to store the link status
  const [linkStatus, setLinkStatus] = useState('onload');

  // Check if the app is in development environment
  const isDevelopment = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplacedev99285';

  // Check if the app is in uat environment
  const isUAT = process.env.REACT_APP_FIREBASE_PROJECTID === 'auitmarketplaceuat88396';

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Function to check if the URL is valid
  const isValidUrl = (url) => {
    if (url === '') return true;
    const urlPattern = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;
    return urlPattern.test(url);
  };

  // Function to add link
  function addLink() {

    // Validate URL
    if (!currentLink || !isValidUrl(currentLink)) return;

    setLinkStatus('pending');

    // Prepare firestore document
    let document = {
      'lastmodifieddate': new Date(),
      'lastmodifiedby': {
        'email': getUser?.emailaddress,
        'givenname': getUser?.givenname,
        'surname': getUser?.surname
      }
    };

    // Handle nested fieldpaths & prepare updated links array
    const updatedLinks = [...links, currentLink];
    const keys = fieldpath.split('.');
    const results = {};


    if (keys.length === 1) {
      results[keys[0]] = updatedLinks;
    }

    else if (keys.length === 2) {
      results[keys[0]] = {
        [keys[1]]: updatedLinks
      };
    }

    else if (keys.length === 3) {
      results[keys[0]] = {
        [keys[1]]: {
          [keys[2]]: updatedLinks
        }
      };
    }

    else if (keys.length === 4) {
      results[keys[0]] = {
        [keys[1]]: {
          [keys[2]]: {
            [keys[3]]: updatedLinks
          }
        }
      };
    }

    else if (keys.length === 5) {
      setLinkStatus('error');
      return;

    }

    // Merge the objects
    Object.assign(document, results);

    // Update Firestore
    WriteDocument(collectionid, documentid, document, true)
      .then(() => {

        setLinkStatus('success');

        // Reset status after 1 second
        return setTimeout(() => {
          setCurrentLink('');
          setLinkStatus('onload');

        }, 1000);

      })
      .catch((error) => {

        console.log('error', error);
        setLinkStatus('error');

      })

  }

  // Function to remove link
  function removeLink(indexToRemove) {

    setLinkStatus('pending');

    const updatedLinks = links.filter((_, index) => index !== indexToRemove);

    // Prepare firestore document
    let document = {
      'lastmodifieddate': new Date(),
      'lastmodifiedby': {
        'email': getUser?.emailaddress,
        'givenname': getUser?.givenname,
        'surname': getUser?.surname
      }
    };

    // Handle nested fieldpaths
    const keys = fieldpath.split('.');
    const results = {};

    if (keys.length === 1) {
      results[keys[0]] = updatedLinks;
    }

    else if (keys.length === 2) {
      results[keys[0]] = {
        [keys[1]]: updatedLinks
      };
    }

    else if (keys.length === 3) {
      results[keys[0]] = {
        [keys[1]]: {
          [keys[2]]: updatedLinks
        }
      };
    }

    else if (keys.length === 4) {
      results[keys[0]] = {
        [keys[1]]: {
          [keys[2]]: {
            [keys[3]]: updatedLinks
          }
        }
      };
    }

    else if (keys.length === 5) {
      setLinkStatus('error');
      return;
    }

    // Merge the objects
    Object.assign(document, results);

    // Update Firestore
    WriteDocument(collectionid, documentid, document, true)
      .then(() => {

        setLinkStatus('success');

        // Reset status after 1 second
        setTimeout(() => {
          setLinkStatus('onload');

        }, 1000);

      })
      .catch((error) => {

        console.log('error', error);
        setLinkStatus('error');

      })

  }

  // Function to Render Input
  const renderInput = () => {

    // eslint-disable-next-line default-case
    switch (linkStatus) {
      case 'onload':
        return (
          <div>
            <div className='flex gap-2'>
              <input type='url' value={currentLink} onChange={(e) => setCurrentLink(e.target.value)} placeholder='https://' className='Input-Field-Text' style={{ width: '100%' }} disabled={disabled}></input>
              <button onClick={() => addLink()} disabled={!currentLink || !isValidUrl(currentLink) || disabled}>
                <img
                  className='min-w-[30px] min-h-[30px] cursor-pointer self-center'
                  src={!currentLink ? AddDisabled : isValidUrl(currentLink) ? Add : errorIcon}
                  alt='Add Icon'
                ></img>
              </button>
            </div>
            {
              currentLink !== '' && !isValidUrl(currentLink) && 
              <label className='text-[#DE0000] font-medium mt-2'>
                Invalid Link
              </label>
            }
          </div>
        );

      case 'pending':
        return (
          <div className='flex flex-row gap-2'>
            <input type='url' value={currentLink} className='Input-Field-Text' style={{ width: '100%' }} disabled></input>
            <img className='w-[30px] h-[30px] self-center' src={pendingIcon} alt='pending-icon'></img>
          </div>
        );

      case 'success':
        return (
          <div className='flex flex-row gap-2'>
            <input type='url' value={currentLink} className='Input-Field-Text' style={{ width: '100%' }} disabled></input>
            <img className='LiveField-Success' src={successIcon} alt='success-icon'></img>
          </div>
        );

      case 'error':
        return (
          <div className='flex flex-col gap-0'>
            <div className='flex flex-row gap-2'>
              <input type='url' value={currentLink} onChange={(e) => setCurrentLink(e.target.value)} className='Input-Field-Text-Error' style={{ width: '100%' }}></input>
              <img className='w-[30px]' src={errorIcon} alt='error-icon'></img>
            </div>
            <label className='text-[#DE0000]'>Unable to save this field. Please try again, or contact us <Link className='underline' target='_blank' to={isDevelopment ? 'https://dev.digitalservices.lendlease.com/help' : isUAT ? 'https://uat.digitalservices.lendlease.com/help' : 'https://digitalservices.lendlease.com/help'}>here</Link> if the issue persists.</label>

          </div>
        );
    }
  };

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <div className='w-full'>

      {/* Input Field */}
      <div className='mb-4'>
        {renderInput()}
      </div>

      <div className='space-y-2'>
        {
          links.map((link, index) => (
            <div key={index} className='flex flex-row justify-between items-center gap-2 '>

              {/* Link */}
              <a href={link} target='_blank' rel='noopener noreferrer' className='px-3 py-2 bg-[#ECFCFC] rounded-lg w-full text-[#028E8C] no-underline hover:underline truncate'>
                {link}
              </a>

              {/* Button */}
              <button onClick={() => disabled ? null : removeLink(index)}>
                <img className='min-w-[30px] min-h-[30px] cursor-pointer self-start' src={disabled ? DeleteDisabledIcon : DeleteIcon} alt='Delete-Icon'></img>
              </button>

            </div>
          ))
        }
      </div>
    </div>
  );
};